import { type ReactNode, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, MoreHorizontal } from 'lucide-react';
import { useTranslations } from '@memorai/i18n';
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarInset,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuIcon,
    SidebarMenuItem,
    SidebarMenuLabel,
    SidebarProvider,
    SidebarRail,
    SidebarTrigger,
    useSidebar,
} from '@memorai/ui/ui/sidebar';
import {
    MemoraiButton,
    MemoraiDrawer,
} from '../../lib/components/memorai';
import { APP } from '../../lib/constants';
import { ROUTES } from '../../lib/routes';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../hooks/queries/useUser';
import {
    getMobilePrimaryItems,
    getNavigationGroups,
    isActivePath,
    type NavigationGroup,
} from './navigation-config';
import { MemoraiLogo, MemoraiLogoMark } from '../brand/MemoraiLogo';

function AppBrand() {
    return (
        <Link to={ROUTES.HOME} className="flex min-h-10 min-w-0 items-center text-primary" aria-label={APP.NAME}>
            <MemoraiLogoMark className="h-14 w-auto lg:hidden" />
            <MemoraiLogo className="hidden h-14 w-auto max-w-[18rem] lg:block" />
        </Link>
    );
}

function SignOutButton({
    collapsed = false,
    onSignedOut,
}: {
    collapsed?: boolean;
    onSignedOut?: () => void;
}) {
    const { t: settingsT } = useTranslations('settings');
    const { signOut } = useAuth();
    const navigate = useNavigate();
    const [pending, setPending] = useState(false);

    const handleSignOut = async () => {
        if (pending) return;
        setPending(true);
        try {
            await signOut();
            onSignedOut?.();
            navigate(ROUTES.LOGIN, { replace: true });
        } finally {
            setPending(false);
        }
    };

    return (
        <MemoraiButton
            type="button"
            variant="ghost"
            onClick={handleSignOut}
            disabled={pending}
            aria-label={collapsed ? settingsT('account.logOut') : undefined}
            title={collapsed ? settingsT('account.logOut') : undefined}
            className={`w-full ${collapsed ? 'justify-center px-0' : 'justify-start'}`}
        >
            <LogOut className="size-4 shrink-0" aria-hidden="true" />
            {collapsed ? null : <span>{settingsT('account.logOut')}</span>}
        </MemoraiButton>
    );
}

function NavigationGroups({
    groups,
    onNavigate,
    variant = 'sidebar',
}: {
    groups: NavigationGroup[];
    onNavigate?: () => void;
    variant?: 'sidebar' | 'drawer';
}) {
    const location = useLocation();

    return (
        <div className="grid gap-4">
            {groups.map((group) => (
                <SidebarGroup key={group.title}>
                    <SidebarGroupLabel>{group.title}</SidebarGroupLabel>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            {group.items.map((item) => {
                                if (!item.to) return null;
                                const Icon = item.icon;
                                const active = isActivePath(location.pathname, item.to, item.exact);

                                return (
                                    <SidebarMenuItem key={`${item.label}-${item.to}`}>
                                        <SidebarMenuButton asChild isActive={active} tooltip={item.label}>
                                            <Link
                                                to={item.to}
                                                onClick={onNavigate}
                                                aria-current={active ? 'page' : undefined}
                                                aria-label={variant === 'sidebar' ? item.label : undefined}
                                                title={variant === 'sidebar' ? item.label : undefined}
                                            >
                                                <SidebarMenuIcon>
                                                    <Icon className="size-4" aria-hidden="true" />
                                                </SidebarMenuIcon>
                                                <SidebarMenuLabel>{item.label}</SidebarMenuLabel>
                                            </Link>
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>
                                );
                            })}
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>
            ))}
        </div>
    );
}

function DesktopSidebar({ groups }: { groups: NavigationGroup[] }) {
    const { isCollapsedLayout } = useSidebar();
    const { t } = useTranslations('navigation');
    const { data: user } = useUser();
    const userName = user?.full_name || user?.email;

    return (
        <Sidebar>
            <SidebarHeader className={`flex items-center gap-3 ${isCollapsedLayout ? 'justify-center' : 'justify-between'}`}>
                {!isCollapsedLayout && userName && (
                    <p className="min-w-0 truncate text-sm font-medium text-foreground-subtle">
                        {t('greeting', { userName })}
                    </p>
                )}
                <SidebarTrigger className="shrink-0" />
            </SidebarHeader>
            <SidebarContent>
                <NavigationGroups groups={groups} />
            </SidebarContent>
            <SidebarFooter>
                <SignOutButton collapsed={isCollapsedLayout} />
            </SidebarFooter>
            <SidebarRail />
        </Sidebar>
    );
}

function AppHeader() {
    return (
        <header className="sticky top-0 z-40 flex h-[var(--app-header-height)] items-center justify-center border-b border-border bg-background/95 px-4 backdrop-blur lg:justify-between lg:px-6">
            <div className="lg:mr-auto">
                <AppBrand />
            </div>
            <div className="flex items-center gap-2" />
        </header>
    );
}

function MobileBottomTabs({
    groups,
    open,
    onToggleNavigation,
    onCloseNavigation,
}: {
    groups: NavigationGroup[];
    open: boolean;
    onToggleNavigation: () => void;
    onCloseNavigation: () => void;
}) {
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslations('navigation');
    const visibleItems = getMobilePrimaryItems(groups);

    return (
        <nav
            className="flex border-t border-border bg-background/95 pb-[env(safe-area-inset-bottom)] backdrop-blur lg:hidden"
            aria-label={t('mobilePrimaryNavigation')}
        >
            {visibleItems.map((item) => {
                if (!item.to) return null;
                const to = item.to;
                const Icon = item.icon;
                const active = isActivePath(location.pathname, to, item.exact);

                return (
                    <button
                        key={`${item.label}-${item.to}`}
                        type="button"
                        aria-current={active ? 'page' : undefined}
                        className={`flex min-h-16 flex-1 flex-col items-center justify-center gap-1 px-1 text-xs font-medium transition-colors ${active ? 'text-primary' : 'text-foreground-subtle hover:text-foreground'}`}
                        onClick={() => {
                            onCloseNavigation();
                            navigate(to);
                        }}
                    >
                        <Icon className="size-5" aria-hidden="true" />
                        <span className="max-w-full truncate">{item.label}</span>
                    </button>
                );
            })}
            <button
                type="button"
                className={`flex min-h-16 flex-1 flex-col items-center justify-center gap-1 px-1 text-xs font-medium transition-colors hover:text-foreground ${open ? 'text-primary' : 'text-foreground-subtle'}`}
                onClick={onToggleNavigation}
                aria-label={t('items.more')}
                aria-expanded={open}
            >
                <MoreHorizontal className="size-5" aria-hidden="true" />
                <span>{t('items.more')}</span>
            </button>
        </nav>
    );
}

function MobileNavigationDrawer({
    open,
    onOpenChange,
    groups,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    groups: NavigationGroup[];
}) {
    const { t } = useTranslations('navigation');

    return (
        <MemoraiDrawer
            open={open}
            onOpenChange={onOpenChange}
            title={t('items.more')}
            titleHidden
            className="bottom-0 z-[110] flex h-dvh max-h-dvh flex-col rounded-t-[2rem] border-foreground/10 border-b-0 bg-background p-0 shadow-2xl"
            contentClassName="flex min-h-0 flex-1 flex-col overflow-hidden p-0"
            showOverlay={false}
        >
            <div className="min-h-0 flex-1 overflow-y-auto p-5">
                <div className="grid gap-5 pt-1">
                    <NavigationGroups groups={groups} onNavigate={() => onOpenChange(false)} variant="drawer" />
                    <div className="grid gap-3 border-t border-border pt-5">
                        <SignOutButton onSignedOut={() => onOpenChange(false)} />
                    </div>
                </div>
            </div>
            <MobileBottomTabs
                groups={groups}
                open={open}
                onToggleNavigation={() => onOpenChange(!open)}
                onCloseNavigation={() => onOpenChange(false)}
            />
        </MemoraiDrawer>
    );
}

export function AppNavigationShell({ children }: { children: ReactNode }) {
    const { t } = useTranslations('navigation');
    const [mobileOpen, setMobileOpen] = useState(false);
    const groups = getNavigationGroups(t);

    return (
        <SidebarProvider>
            <div className="min-h-dvh bg-background text-foreground">
                <AppHeader />
                <MobileNavigationDrawer open={mobileOpen} onOpenChange={setMobileOpen} groups={groups} />
                <div className="lg:flex lg:min-h-[calc(100dvh-var(--app-header-height))]">
                    <DesktopSidebar groups={groups} />
                    <SidebarInset>
                        <div className="mx-auto max-w-6xl px-4 py-6 pb-24 lg:px-8 lg:py-8">
                            {children}
                        </div>
                    </SidebarInset>
                </div>
                {!mobileOpen && (
                    <div className="fixed inset-x-0 bottom-0 z-[120] lg:hidden">
                        <MobileBottomTabs
                            groups={groups}
                            open={mobileOpen}
                            onToggleNavigation={() => setMobileOpen((current) => !current)}
                            onCloseNavigation={() => setMobileOpen(false)}
                        />
                    </div>
                )}
            </div>
        </SidebarProvider>
    );
}
